import React from 'react';
import { motion } from 'framer-motion';
import type { TransitionStage } from '@/lib/hooks/use-geographical-transition';
import { IMAGE_REVEAL_DURATION, ZOOM_OUT_DURATION } from '@/lib/hooks/use-geographical-transition';

interface GeographicalTransitionOverlayProps {
    isAnimating: boolean;
    stage: TransitionStage;
    sourceRect: DOMRect | null;
    markerPosition: { x: number; y: number } | null;
    reconstructionImageUrl?: string;
    onAnimationComplete: () => void;
}

export function GeographicalTransitionOverlay({
    isAnimating,
    stage,
    markerPosition,
    reconstructionImageUrl,
    onAnimationComplete
}: GeographicalTransitionOverlayProps) {
    if (!isAnimating || !reconstructionImageUrl) return null;

    // Animation Variants
    const reconstructionVariants = {
        initial: {
            opacity: 0,
            scale: 1.2,
            filter: 'brightness(0.5) blur(10px)',
        },
        reveal: {
            opacity: 1,
            scale: 1,
            filter: 'brightness(1) blur(0px)',
            x: 0,
            y: 0,
            transition: { duration: IMAGE_REVEAL_DURATION / 1000, ease: "easeOut" as any }
        },
        zoomOut: {
            scale: 0.1,
            opacity: 0,
            filter: 'brightness(1) blur(0px)',
            // Shrink towards the map marker if we have it, otherwise towards center
            x: markerPosition ? markerPosition.x - (window.innerWidth / 2) : 0,
            y: markerPosition ? markerPosition.y - (window.innerHeight / 2) : 0,
            transition: {
                duration: ZOOM_OUT_DURATION / 1000,
                ease: [0.4, 0, 0.2, 1] as any
            }
        }
    };

    const currentVariant = stage === 'image-reveal' ? 'reveal' : stage === 'zoom-out' ? 'zoomOut' : 'initial';

    return (
        <motion.div
            className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden flex items-center justify-center"
            initial={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
            animate={{
                backgroundColor: stage === 'image-reveal'
                    ? 'rgba(0, 0, 0, 0.4)'
                    : 'rgba(0, 0, 0, 0)'
            }}
            transition={{ duration: stage === 'zoom-out' ? ZOOM_OUT_DURATION / 1000 : 0.2 }}
        >
            {/* Reconstruction Image Layer */}
            <motion.div
                className="w-full h-full bg-cover bg-center shadow-2xl"
                style={{
                    backgroundImage: `url(${reconstructionImageUrl})`,
                    position: 'absolute',
                }}
                variants={reconstructionVariants}
                initial="initial"
                animate={currentVariant}
                onAnimationComplete={(definition) => {
                    if (definition === 'zoomOut') {
                        onAnimationComplete();
                    }
                }}
            />

            {/* Cinematic Vignette Overlay during reveal */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: stage === 'image-reveal' ? 1 : 0 }}
                transition={{ duration: 0.5 }}
            />
        </motion.div>
    );
}
